import React, { useState } from "react";
import { tierRepo, rewardRepo, pointRepo, customerRepo } from "../../services";
import { adjustPoints, sortTiers } from "../../services/loyalty";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, Card, DataTable, Input, Modal, PageHead, ProgressBar, Select, StatCard, Textarea, formatDate, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";

const EMPTY_TIER = { id: "", key: "", name: "", minSpend: "0", pointRate: "1", discountPercent: "0", color: "#6366f1", benefits: "" };
const EMPTY_REWARD = { id: "", name: "", points: "", stock: "" };
const EMPTY_ADJUST = { customerId: "", direction: "earn", points: "", detail: "" };
const slug = (text) => String(text || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

export default function Membership() {
  const { orgId, toast } = useApp();
  const { tx } = useI18n();
  const { data: rawTiers, reload: reloadTiers } = useAsync(() => tierRepo.all(orgId), [orgId], []);
  const { data: rewards, reload: reloadRewards } = useAsync(() => rewardRepo.all(orgId), [orgId], []);
  const { data: points, loading, reload: reloadPoints } = useAsync(() => pointRepo.list(orgId, { page: 1, perPage: 15, sortBy: "createdAt", sortDir: "desc" }), [orgId]);
  const { data: customers } = useAsync(() => customerRepo.all(orgId), [orgId], []);
  const [tierForm, setTierForm] = useState(null);
  const [rewardForm, setRewardForm] = useState(null);
  const [adjustForm, setAdjustForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const tiers = sortTiers(rawTiers || []);
  // เกณฑ์สูงสุดของร้านนี้ = 100% ของแถบ (ไม่ hardcode)
  const topSpend = tiers.reduce((max, tier) => Math.max(max, Number(tier.minSpend) || 0), 0);
  const spendText = (value) => (Number(value) || 0).toLocaleString();

  const run = async (job, done) => {
    setSaving(true);
    try {
      await job();
      done();
    } catch (error) {
      toast(error?.message || tx("บันทึกไม่สำเร็จ", "Save failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const openTier = (tier) =>
    setTierForm(tier ? { ...EMPTY_TIER, ...tier, minSpend: String(tier.minSpend ?? 0), pointRate: String(tier.pointRate ?? 1), discountPercent: String(tier.discountPercent ?? 0), benefits: (tier.benefits || []).join("\n") } : EMPTY_TIER);

  const saveTier = () =>
    run(async () => {
      const name = tierForm.name.trim();
      const minSpend = Number(tierForm.minSpend);
      const pointRate = Number(tierForm.pointRate);
      const discountPercent = Number(tierForm.discountPercent);
      if (!name) throw new Error(tx("กรอกชื่อระดับ", "Tier name required"));
      if (!(minSpend >= 0) || !(pointRate > 0) || !(discountPercent >= 0 && discountPercent <= 100)) {
        throw new Error(tx("ตรวจเกณฑ์ยอดใช้จ่าย อัตราแต้ม และส่วนลด (0-100%) ให้ถูกต้อง", "Check spend, point rate and discount (0-100%)"));
      }
      const key = tierForm.id ? tierForm.key : slug(name) || `tier_${Date.now().toString(36)}`;
      if ((rawTiers || []).some((row) => row.key === key && row.id !== tierForm.id)) throw new Error(tx("มีระดับชื่อนี้อยู่แล้ว", "Tier already exists"));
      const payload = { key, name, minSpend, pointRate, discountPercent, color: tierForm.color || "#6366f1", benefits: tierForm.benefits.split("\n").map((line) => line.trim()).filter(Boolean) };
      if (tierForm.id) await tierRepo.update(orgId, tierForm.id, payload);
      else await tierRepo.create(orgId, payload);
    }, () => {
      setTierForm(null);
      reloadTiers();
      toast(tx("บันทึกระดับสมาชิกแล้ว", "Tier saved"), "success");
    });

  const removeTier = async (tier) => {
    if (!window.confirm(tx(`ลบระดับ ${tier.name}? ลูกค้าที่อยู่ระดับนี้จะไม่ถูกเปลี่ยนอัตโนมัติ`, `Delete tier ${tier.name}?`))) return;
    await run(() => tierRepo.remove(orgId, tier.id), () => {
      reloadTiers();
      toast(tx("ลบระดับแล้ว", "Tier deleted"), "success");
    });
  };

  const saveReward = () =>
    run(async () => {
      const name = rewardForm.name.trim();
      const pointsCost = Number(rewardForm.points);
      const stock = Number(rewardForm.stock);
      if (!name) throw new Error(tx("กรอกชื่อของรางวัล", "Reward name required"));
      if (!(pointsCost > 0) || !(stock >= 0) || !Number.isInteger(pointsCost) || !Number.isInteger(stock)) {
        throw new Error(tx("แต้มที่ใช้แลกต้องมากกว่า 0 และสต็อกต้องเป็นจำนวนเต็มไม่ติดลบ", "Points must be > 0 and stock a non-negative integer"));
      }
      const payload = { name, points: pointsCost, stock };
      if (rewardForm.id) await rewardRepo.update(orgId, rewardForm.id, payload);
      else await rewardRepo.create(orgId, payload);
    }, () => {
      setRewardForm(null);
      reloadRewards();
      toast(tx("บันทึกของรางวัลแล้ว", "Reward saved"), "success");
    });

  const removeReward = async (reward) => {
    if (!window.confirm(tx(`ลบของรางวัล ${reward.name}?`, `Delete reward ${reward.name}?`))) return;
    await run(() => rewardRepo.remove(orgId, reward.id), () => {
      reloadRewards();
      toast(tx("ลบของรางวัลแล้ว", "Reward deleted"), "success");
    });
  };

  const saveAdjust = () =>
    run(
      () => adjustPoints({ orgId, customerId: adjustForm.customerId, direction: adjustForm.direction, points: adjustForm.points, detail: adjustForm.detail.trim() }),
      () => {
        setAdjustForm(null);
        reloadPoints();
        toast(tx("ปรับแต้มเรียบร้อย", "Points adjusted"), "success");
      }
    );

  return (
    <>
      <PageHead
        title={tx("สมาชิก & แต้มสะสม", "Membership & points")}
        subtitle={tx("ตั้งค่าระดับสมาชิกและของรางวัลสำหรับแลกแต้ม", "Configure tiers and reward catalog")}
        actions={<Button variant="primary" onClick={() => setAdjustForm(EMPTY_ADJUST)}>{tx("ให้ / หักแต้มลูกค้า", "Adjust points")}</Button>}
      />

      <div className="stat-grid">
        {(tiers || []).map((tier) => (
          <StatCard key={tier.id} icon={<Icon name="badge" />} label={tier.name} value={`${tier.discountPercent}%`} hint={tx(`ยอดใช้จ่ายขั้นต่ำ ฿${spendText(tier.minSpend)}`, `Min spend ฿${spendText(tier.minSpend)}`)} />
        ))}
      </div>

      <div className="grid-2">
        <Card title={tx("ระดับสมาชิก", "Membership tiers")} actions={<Button size="sm" onClick={() => openTier(null)}>+ {tx("เพิ่มระดับ", "Add tier")}</Button>}>
          {!tiers.length ? <div className="u-muted u-fs-13">{tx("ยังไม่มีระดับสมาชิก", "No tiers yet")}</div> : null}
          {tiers.map((tier) => (
            <div key={tier.id} className="u-mb-16">
              <div className="u-between u-mb-8">
                <span className="u-strong" style={{ color: tier.color }}>{tier.name}</span>
                <span className="u-fs-13 u-muted">
                  x{tier.pointRate} {tx("แต้ม", "pts")}{" "}
                  <Button size="sm" onClick={() => openTier(tier)}>{tx("แก้ไข", "Edit")}</Button>{" "}
                  <Button size="sm" onClick={() => removeTier(tier)}>{tx("ลบ", "Delete")}</Button>
                </span>
              </div>
              <ProgressBar percent={topSpend > 0 ? Math.min(100, ((Number(tier.minSpend) || 0) / topSpend) * 100) : 100} color={tier.color} />
              <div className="u-fs-12 u-muted u-mt-8">{(tier.benefits || []).join(" · ")}</div>
            </div>
          ))}
        </Card>

        <Card title={tx("ของรางวัลสำหรับแลกแต้ม", "Reward catalog")} actions={<Button size="sm" onClick={() => setRewardForm(EMPTY_REWARD)}>+ {tx("เพิ่มของรางวัล", "Add reward")}</Button>}>
          {!(rewards || []).length ? <div className="u-muted u-fs-13">{tx("ยังไม่มีของรางวัล", "No rewards yet")}</div> : null}
          {(rewards || []).map((reward) => (
            <div key={reward.id} className="u-between" style={{ padding: "9px 0", borderBottom: "1px solid var(--c-slate-100)" }}>
              <div>
                <div className="u-strong u-fs-13">{reward.name}</div>
                <div className="u-fs-12 u-muted">{tx("คงเหลือ", "Stock")} {reward.stock}</div>
              </div>
              <div className="u-strong u-indigo">
                {reward.points} pts{" "}
                <Button size="sm" onClick={() => setRewardForm({ ...reward, points: String(reward.points), stock: String(reward.stock) })}>{tx("แก้ไข", "Edit")}</Button>{" "}
                <Button size="sm" onClick={() => removeReward(reward)}>{tx("ลบ", "Delete")}</Button>
              </div>
            </div>
          ))}
        </Card>
      </div>

      <DataTable
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          { key: "customerName", label: tx("ลูกค้า", "Customer") },
          { key: "type", label: tx("ประเภท", "Type"), render: (row) => (row.type === "earn" ? `⬆️ ${tx("สะสม", "Earn")}` : row.type === "adjust" ? `✏️ ${tx("ปรับแต้ม", "Adjust")}` : `⬇️ ${tx("แลก", "Redeem")}`) },
          { key: "points", label: tx("แต้ม", "Points"), align: "right" },
          { key: "detail", label: tx("รายละเอียด", "Detail") },
        ]}
        rows={points?.rows || []}
        loading={loading}
        emptyText={tx("ยังไม่มีประวัติแต้ม", "No point history")}
      />

      <Modal
        open={!!tierForm}
        title={tierForm?.id ? tx("แก้ไขระดับสมาชิก", "Edit tier") : tx("เพิ่มระดับสมาชิก", "New tier")}
        onClose={() => setTierForm(null)}
        footer={<><Button onClick={() => setTierForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={saveTier}>{tx("บันทึก", "Save")}</Button></>}
      >
        {tierForm ? (
          <>
            <Input label={tx("ชื่อระดับ", "Tier name")} value={tierForm.name} onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })} />
            <Input label={tx("ยอดใช้จ่ายสะสมขั้นต่ำ (บาท)", "Minimum lifetime spend (THB)")} type="number" min="0" value={tierForm.minSpend} onChange={(e) => setTierForm({ ...tierForm, minSpend: e.target.value })} />
            <Input label={tx("ตัวคูณแต้ม", "Point multiplier")} type="number" min="0" step="0.1" value={tierForm.pointRate} onChange={(e) => setTierForm({ ...tierForm, pointRate: e.target.value })} />
            <Input label={tx("ส่วนลด (%)", "Discount (%)")} type="number" min="0" max="100" value={tierForm.discountPercent} onChange={(e) => setTierForm({ ...tierForm, discountPercent: e.target.value })} />
            <Input label={tx("สี", "Color")} type="color" value={tierForm.color} onChange={(e) => setTierForm({ ...tierForm, color: e.target.value })} />
            <Textarea label={tx("สิทธิประโยชน์ (บรรทัดละ 1 ข้อ)", "Benefits (one per line)")} rows={4} value={tierForm.benefits} onChange={(e) => setTierForm({ ...tierForm, benefits: e.target.value })} />
          </>
        ) : null}
      </Modal>

      <Modal
        open={!!rewardForm}
        title={rewardForm?.id ? tx("แก้ไขของรางวัล", "Edit reward") : tx("เพิ่มของรางวัล", "New reward")}
        onClose={() => setRewardForm(null)}
        footer={<><Button onClick={() => setRewardForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={saveReward}>{tx("บันทึก", "Save")}</Button></>}
      >
        {rewardForm ? (
          <>
            <Input label={tx("ชื่อของรางวัล", "Reward name")} value={rewardForm.name} onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })} />
            <Input label={tx("แต้มที่ใช้แลก", "Points required")} type="number" min="1" value={rewardForm.points} onChange={(e) => setRewardForm({ ...rewardForm, points: e.target.value })} />
            <Input label={tx("จำนวนคงเหลือ", "Stock")} type="number" min="0" value={rewardForm.stock} onChange={(e) => setRewardForm({ ...rewardForm, stock: e.target.value })} />
          </>
        ) : null}
      </Modal>

      <Modal
        open={!!adjustForm}
        title={tx("ให้ / หักแต้มลูกค้า", "Adjust customer points")}
        onClose={() => setAdjustForm(null)}
        footer={<><Button onClick={() => setAdjustForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={saveAdjust}>{tx("บันทึก", "Save")}</Button></>}
      >
        {adjustForm ? (
          <>
            <Select
              label={tx("ลูกค้า", "Customer")}
              placeholder={tx("เลือกลูกค้า", "Select customer")}
              options={(customers || []).map((c) => ({ value: c.id, label: `${c.name} (${Number(c.points) || 0} pts)` }))}
              value={adjustForm.customerId}
              onChange={(e) => setAdjustForm({ ...adjustForm, customerId: e.target.value })}
            />
            <Select
              label={tx("รายการ", "Action")}
              options={[{ value: "earn", label: tx("ให้แต้ม (+)", "Add points (+)") }, { value: "deduct", label: tx("หักแต้ม (−)", "Deduct points (−)") }]}
              value={adjustForm.direction}
              onChange={(e) => setAdjustForm({ ...adjustForm, direction: e.target.value })}
            />
            <Input label={tx("จำนวนแต้ม", "Points")} type="number" min="1" value={adjustForm.points} onChange={(e) => setAdjustForm({ ...adjustForm, points: e.target.value })} />
            <Input label={tx("เหตุผล / รายละเอียด", "Reason")} value={adjustForm.detail} onChange={(e) => setAdjustForm({ ...adjustForm, detail: e.target.value })} />
          </>
        ) : null}
      </Modal>
    </>
  );
}
